import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, RefreshCw, Star, Trash2 } from "lucide-react";

import { useApi } from "../api/useApi";
import { cagr, shortName } from "../lib/format";
import {
  dailyPriceChange,
  quoteSessionLine,
  quoteTime,
  signedMoney,
} from "../lib/quoteFormat";
import { useVisiblePolling } from "../lib/useVisiblePolling";
import type {
  BrokerageAccountSummary,
  DrawdownState,
  MarketTarget,
  WatchlistItem,
} from "../types";
import {
  AccountRelationshipBars,
  AccountRelationshipBarsSkeleton,
} from "../components/AccountRelationshipBars";
import { TrackedSecurityPanel } from "./TrackedSecurityPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function money(value: number | undefined | null, currency = "USD"): string {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}

function percent(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  return `${value.toFixed(2)}%`;
}

/** Signed percent with a sign prefix, for period returns (e.g. "+12.3%", "-4.1%"). */
function signedPercent(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

/** Tailwind text color for a signed figure: green up, red down, muted when absent. */
function changeColor(value: number | undefined | null): string {
  if (value === undefined || value === null) return "text-muted-foreground";
  if (value > 0) return "text-success";
  if (value < 0) return "text-destructive";
  return "";
}

/** Compact "LABEL value" chip for the mobile watchlist cards. Fixed layout per grid cell
    keeps all return periods on one line without allowing long values to widen a row. */
function ReturnTag({ label, value }: { label: string; value: number | undefined | null }) {
  return (
    <span className="flex min-w-0 flex-col leading-none">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={"mt-0.5 truncate text-[11px] tabular-nums " + changeColor(value)}>
        {signedPercent(value)}
      </span>
    </span>
  );
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

/** Placeholder rows shown during the first watchlist load so the layout does not jump
    from an empty state to populated rows. Mirrors both the mobile card and desktop table
    shapes at the same row heights as the real content. */
function WatchlistSkeleton({ rows = 4 }: { rows?: number }) {
  const items = Array.from({ length: rows });
  return (
    <div data-testid="watchlist-skeleton" aria-busy="true">
      {/* Mobile */}
      <ul className="divide-y divide-border md:hidden">
        {items.map((_, i) => (
          <li key={i} className="py-1.5">
            <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto_auto_1.5rem] items-start gap-2">
              <Skeleton className="size-6" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-28 max-w-full" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
              <Skeleton className="ml-auto size-6" />
            </div>
          </li>
        ))}
      </ul>
      {/* Desktop */}
      <div className="hidden md:block">
        <Table className="[&_td:not(:first-child)]:border-l [&_td]:border-border/40 [&_th:not(:first-child)]:border-l [&_th]:border-border/40">
          <TableHeader>
            <TableRow>
              <TableHead className="h-8">Symbol</TableHead>
              <TableHead className="h-8 text-right">Last price</TableHead>
              <TableHead className="h-8 text-right">Change</TableHead>
              <TableHead className="h-8 text-right">Drawdown</TableHead>
              <TableHead className="h-8 text-right">YTD</TableHead>
              <TableHead className="h-8 text-right">1Y</TableHead>
              <TableHead className="h-8 text-right">3Y CAGR</TableHead>
              <TableHead className="h-8 text-right">5Y CAGR</TableHead>
              <TableHead className="h-8 text-right">10Y CAGR</TableHead>
              <TableHead className="h-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((_, i) => (
              <TableRow key={i}>
                <TableCell className="space-y-1 py-1.5">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-2.5 w-28" />
                </TableCell>
                {Array.from({ length: 2 }).map((_, j) => (
                  <TableCell key={j} className="space-y-1 py-1.5">
                    <Skeleton className="ml-auto h-4 w-16" />
                    <Skeleton className="ml-auto h-2.5 w-12" />
                  </TableCell>
                ))}
                {Array.from({ length: 6 }).map((_, j) => (
                  <TableCell key={j} className="py-1.5">
                    <Skeleton className="ml-auto h-3 w-12" />
                  </TableCell>
                ))}
                <TableCell className="py-1.5">
                  <Skeleton className="ml-auto size-7" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AccountOverviewSkeleton() {
  return (
    <Card className="gap-0 py-0" data-testid="account-overview-skeleton" aria-busy="true">
      <CardContent className="space-y-3 p-3">
        <Skeleton className="h-3 w-32" />
        <AccountRelationshipBarsSkeleton rows={2} compact />
      </CardContent>
    </Card>
  );
}

function TrackedSecuritySkeleton() {
  return (
    <Card className="gap-0 py-0" data-testid="tracked-security-skeleton" aria-busy="true">
      <CardContent className="flex items-center gap-3 p-3">
        <Skeleton className="size-4" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 max-w-64 flex-1" />
        <Skeleton className="h-5 w-28 rounded-full" />
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const api = useApi();
  const [summary, setSummary] = useState<BrokerageAccountSummary | null>(null);
  const [target, setTarget] = useState<MarketTarget | null>(null);
  const [state, setState] = useState<DrawdownState | null>(null);
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [newSymbol, setNewSymbol] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  // Each section resolves independently so a slow brokerage/state request does not keep
  // the already-available watchlist hidden behind a page-wide loading gate.
  const [accountLoading, setAccountLoading] = useState(true);
  const [targetLoading, setTargetLoading] = useState(true);
  const [watchlistLoading, setWatchlistLoading] = useState(true);
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(() => new Set());
  const [refreshing, setRefreshing] = useState(false);

  const loadWatchlist = useCallback(async (background = false) => {
    if (!background) setRefreshing(true);
    try {
      setWatchlist(await api.listWatchlist());
    } catch (err) {
      if (!background) {
        setStatus(err instanceof Error ? err.message : "Failed to load watchlist");
      }
    } finally {
      if (!background) setRefreshing(false);
    }
  }, [api]);

  const refreshWatchlist = async () => {
    setRefreshing(true);
    setStatus("");
    try {
      setWatchlist(await api.refreshWatchlist());
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Failed to refresh watchlist");
    } finally {
      setRefreshing(false);
    }
  };

  const pollDashboard = useCallback(async () => {
    const requests: Promise<unknown>[] = [loadWatchlist(true)];
    if (target) {
      requests.push(
        api.getState(target.market_target_id).then(setState).catch(() => undefined),
      );
    }
    await Promise.all(requests);
  }, [api, loadWatchlist, target]);

  useVisiblePolling(pollDashboard);

  const load = useCallback(async () => {
    const accountRequest = (async () => {
      try {
        setSummary(await api.getBrokerageAccounts());
      } catch {
        setSummary(null);
      } finally {
        setAccountLoading(false);
      }
    })();

    const targetRequest = (async () => {
      try {
        const targets = await api.listMarketTargets();
        if (targets[0]) {
          setTarget(targets[0]);
          // The state endpoint also resolves account LTV. Let the account request fill
          // the shared Plaid cache first so simultaneous cache misses cannot duplicate
          // an external Plaid call in separate Lambda executions.
          await accountRequest;
          setState(await api.getState(targets[0].market_target_id));
        }
      } catch {
        // No target yet; the overview still renders the watchlist.
      } finally {
        setTargetLoading(false);
      }
    })();

    const watchlistRequest = loadWatchlist().finally(() => setWatchlistLoading(false));
    await Promise.all([accountRequest, targetRequest, watchlistRequest]);
  }, [api, loadWatchlist]);

  useEffect(() => {
    void load();
  }, [load]);

  const addSymbol = async () => {
    const symbol = newSymbol.trim().toUpperCase();
    if (!symbol) return;
    setBusy(true);
    setStatus("");
    try {
      await api.addWatchlist(symbol);
      setNewSymbol("");
      await loadWatchlist();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not add symbol");
    } finally {
      setBusy(false);
    }
  };

  const removeSymbol = async (symbol: string) => {
    setStatus("");
    try {
      await api.removeWatchlist(symbol);
      await loadWatchlist();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not remove symbol");
    }
  };

  const headroom = state?.margin_headroom;
  const trackedSymbol = target?.symbol?.toUpperCase() ?? null;
  const isTracked = (symbol: string) => symbol.toUpperCase() === trackedSymbol;
  const latestFetch = watchlist
    .map((w) => w.price_fetched_at)
    .filter(Boolean)
    .sort()
    .at(-1);

  // Tracked (starred) securities always sort to the top; the rest keep their existing
  // order (stable sort preserves it). This is display-only, not persisted.
  const sortedWatchlist = [...watchlist].sort((a, b) => {
    const at = isTracked(a.symbol) ? 0 : 1;
    const bt = isTracked(b.symbol) ? 0 : 1;
    return at - bt;
  });

  // The IEX reference price updates outside the regular session, so flag the session the
  // displayed prices were captured in: "extended" (pre/after-hours on a trading day) shows
  // "After hours"; "closed" (weekend or exchange holiday) shows "Market closed". A regular
  // session shows no tag. "closed" takes precedence if any priced row reports it.
  const priced = watchlist.filter((w) => w.latest_price != null);
  const sessionTag: "After hours" | "Market closed" | null = priced.some(
    (w) => w.price_session === "closed",
  )
    ? "Market closed"
    : priced.some((w) => w.price_session === "extended")
      ? "After hours"
      : null;

  return (
    <section className="space-y-3">
      {/* The relationship bars carry both the account values and their context, avoiding
          a separate totals grid that repeats the same figures. */}
      {accountLoading ? (
        <AccountOverviewSkeleton />
      ) : summary ? (
        <Card className="gap-0 py-0">
          <CardContent className="space-y-2 p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {summary.source ?? "account"}
              {summary.as_of ? ` · ${timeAgo(summary.as_of)}` : ""}
            </p>
            <AccountRelationshipBars
              summary={summary}
              headroom={headroom}
              compact
              showPortfolioDetails={false}
            />
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 py-0">
          <CardContent className="space-y-2 p-3">
            <p className="text-sm font-medium">Connect your account to track margin risk</p>
            <p className="text-xs text-muted-foreground">
              Link a brokerage through Plaid, or enter your account value and margin
              balance by hand. Smart Margin uses these to compute your loan-to-value and
              headroom against the alert ladder.
            </p>
            <Button asChild size="sm" className="h-8">
              <Link to="/link">Connect account</Link>
            </Button>
          </CardContent>
        </Card>
      )}
      {/* Tracked-security visibility: drawdown, margin-risk severity, and the ladder. */}
      {targetLoading ? (
        <TrackedSecuritySkeleton />
      ) : (
        target && state && (
          <TrackedSecurityPanel target={target} state={state} showMarginUsage={!summary} />
        )
      )}

      {/* Watchlist — the focus of the page, kept high and dense. */}
      <Card className="gap-3 py-3">
        <CardHeader className="flex flex-col gap-3 space-y-0 py-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-base">Watchlist</CardTitle>
            <div className="flex min-h-4 flex-wrap items-center gap-1 text-xs text-muted-foreground">
              {watchlistLoading ? (
                <Skeleton className="h-3 w-32" />
              ) : (
                <>Prices as of {timeAgo(latestFetch)}</>
              )}
              {sessionTag && (
                <span className="rounded bg-warning/15 px-1.5 py-0.5 font-medium text-warning">
                  {sessionTag}
                </span>
              )}
              {trackedSymbol && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  {" · "}
                  <Star className="size-3 fill-primary text-primary" />
                  tracked
                </span>
              )}
            </div>
          </div>
          <form
            className="flex w-full items-center gap-2 sm:w-auto"
            onSubmit={(e) => {
              e.preventDefault();
              void addSymbol();
            }}
          >
            <Input
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value)}
              aria-label="add-symbol"
              placeholder="Add symbol"
              className="h-8 flex-1 sm:w-32 sm:flex-none"
            />
            <Button type="submit" size="sm" className="h-8" disabled={busy || !newSymbol.trim()}>
              <Plus className="size-4" />
              Add
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="size-8 shrink-0"
              aria-label="refresh-watchlist"
              disabled={refreshing}
              onClick={() => void refreshWatchlist()}
            >
              <RefreshCw className={"size-4" + (refreshing ? " animate-spin" : "")} />
            </Button>
          </form>
        </CardHeader>
        <CardContent className="pt-0">
          {watchlistLoading ? (
            <WatchlistSkeleton />
          ) : watchlist.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Your watchlist is empty. Type a ticker in the Add field above (for example
              SPY or AAPL) to start tracking its price, drawdown, and returns.
            </p>
          ) : (
            <>
              {/* Mobile: stacked cards (a wide numeric table does not fit a phone). */}
              <ul className="divide-y divide-border md:hidden" data-testid="watchlist-mobile">
                {sortedWatchlist.map((w) => (
                  <li
                    key={w.symbol}
                    className={
                      "py-1.5 " +
                      (isTracked(w.symbol) ? "-mx-2 rounded-md bg-primary/5 px-2" : "")
                    }
                    data-testid="watchlist-row"
                  >
                    <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto_auto_1.5rem] items-start gap-2">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground"
                        aria-label={`details-${w.symbol}`}
                        aria-expanded={expandedSymbols.has(w.symbol)}
                        onClick={() =>
                          setExpandedSymbols((current) => {
                            const next = new Set(current);
                            if (next.has(w.symbol)) next.delete(w.symbol);
                            else next.add(w.symbol);
                            return next;
                          })
                        }
                      >
                        {expandedSymbols.has(w.symbol) ? (
                          <ChevronDown className="size-3.5" />
                        ) : (
                          <ChevronRight className="size-3.5" />
                        )}
                      </Button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-medium">
                          {isTracked(w.symbol) && (
                            <Star
                              className="size-3.5 fill-primary text-primary"
                              aria-label="tracked security"
                            />
                          )}
                          {w.symbol}
                        </div>
                        {w.name && (
                          <div
                            className="truncate text-xs text-muted-foreground"
                            title={w.name}
                          >
                            {shortName(w.name)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-16 text-right tabular-nums">
                        <div className="font-medium">{money(w.latest_price)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {quoteTime(w.price_fetched_at)}
                        </div>
                      </div>
                      <div
                        className={
                          "min-w-16 text-right tabular-nums " +
                          changeColor(w.today_change_percent)
                        }
                      >
                        <div className="font-medium">{signedMoney(dailyPriceChange(w))}</div>
                        <div className="text-xs">{signedPercent(w.today_change_percent)}</div>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground hover:text-destructive"
                        aria-label={`remove-${w.symbol}`}
                        onClick={() => void removeSymbol(w.symbol)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                    {expandedSymbols.has(w.symbol) && (
                      <div className="mt-1 pl-8" data-testid={`details-${w.symbol}`}>
                        {quoteSessionLine(w) && (
                          <div className="mb-1 truncate text-[10px] text-muted-foreground">
                            {quoteSessionLine(w)}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                          <ReturnTag label="DD" value={w.drawdown_percent} />
                          <ReturnTag label="YTD" value={w.returns?.ytd} />
                          <ReturnTag label="1Y" value={w.returns?.one_year} />
                          <ReturnTag label="3Y CAGR" value={cagr(w.returns?.three_year, 3)} />
                          <ReturnTag label="5Y CAGR" value={cagr(w.returns?.five_year, 5)} />
                          <ReturnTag label="10Y CAGR" value={cagr(w.returns?.ten_year, 10)} />
                        </div>
                        <p className="mt-2 text-[10px] text-muted-foreground">
                          YTD and 1Y are cumulative; 3Y, 5Y, and 10Y CAGR are annualized.
                          All returns include reinvested dividends. DD is drawdown from the all-time high.
                        </p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {/* Desktop/tablet: the full table with faint column separators. */}
              <div className="hidden overflow-x-auto md:block">
                <Table className="[&_td:not(:first-child)]:border-l [&_td]:border-border/40 [&_th:not(:first-child)]:border-l [&_th]:border-border/40">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8">Symbol</TableHead>
                      <TableHead className="h-8 text-right">Last price</TableHead>
                      <TableHead className="h-8 text-right">Change</TableHead>
                      <TableHead className="h-8 text-right">Drawdown</TableHead>
                      <TableHead
                        className="h-8 text-right"
                        title="Total return (dividends reinvested) since prior year-end"
                      >
                        YTD
                      </TableHead>
                      <TableHead
                        className="h-8 text-right"
                        title="1-year total return (dividends reinvested)"
                      >
                        1Y
                      </TableHead>
                      <TableHead
                        className="h-8 text-right"
                        title="3-year compound annual growth rate (dividends reinvested)"
                      >
                        3Y CAGR
                      </TableHead>
                      <TableHead
                        className="h-8 text-right"
                        title="5-year compound annual growth rate (dividends reinvested)"
                      >
                        5Y CAGR
                      </TableHead>
                      <TableHead
                        className="h-8 text-right"
                        title="10-year compound annual growth rate (dividends reinvested)"
                      >
                        10Y CAGR
                      </TableHead>
                      <TableHead className="h-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedWatchlist.map((w) => (
                      <TableRow
                        key={w.symbol}
                        data-testid="watchlist-row-desktop"
                        className={isTracked(w.symbol) ? "bg-primary/5" : undefined}
                      >
                        <TableCell className="py-1.5">
                          <div
                            className="flex items-center gap-1.5 font-medium"
                            title={w.name ?? undefined}
                          >
                            {isTracked(w.symbol) && (
                              <Star
                                className="size-3.5 shrink-0 fill-primary text-primary"
                                aria-label="tracked security"
                              />
                            )}
                            {w.symbol}
                          </div>
                          {w.name && (
                            <div
                              className="max-w-[11rem] truncate text-[11px] text-muted-foreground"
                              title={w.name}
                            >
                              {shortName(w.name)}
                            </div>
                          )}
                          {quoteSessionLine(w) && (
                            <div className="truncate text-[10px] text-muted-foreground">
                              {quoteSessionLine(w)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">
                          <div>{money(w.latest_price)}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {quoteTime(w.price_fetched_at)}
                          </div>
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " +
                            changeColor(w.today_change_percent)
                          }
                        >
                          <div>{signedMoney(dailyPriceChange(w))}</div>
                          <div className="text-[10px]">
                            {signedPercent(w.today_change_percent)}
                          </div>
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " + changeColor(w.drawdown_percent)
                          }
                        >
                          {percent(w.drawdown_percent)}
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " + changeColor(w.returns?.ytd)
                          }
                        >
                          {signedPercent(w.returns?.ytd)}
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " + changeColor(w.returns?.one_year)
                          }
                        >
                          {signedPercent(w.returns?.one_year)}
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " + changeColor(cagr(w.returns?.three_year, 3))
                          }
                        >
                          {signedPercent(cagr(w.returns?.three_year, 3))}
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " + changeColor(cagr(w.returns?.five_year, 5))
                          }
                        >
                          {signedPercent(cagr(w.returns?.five_year, 5))}
                        </TableCell>
                        <TableCell
                          className={
                            "py-1.5 text-right tabular-nums " +
                            changeColor(cagr(w.returns?.ten_year, 10))
                          }
                          title="10-year compound annual growth rate"
                        >
                          {signedPercent(cagr(w.returns?.ten_year, 10))}
                        </TableCell>
                        <TableCell className="py-1.5 text-right">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="size-7 text-muted-foreground hover:text-destructive"
                            aria-label={`remove-${w.symbol}-desktop`}
                            onClick={() => void removeSymbol(w.symbol)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                YTD and 1Y are cumulative; 3Y, 5Y, and 10Y CAGR are annualized.
                All returns include reinvested dividends. Change
                is the price-only move for the latest session.
              </p>
            </>
          )}
          {status && <p className="mt-3 text-sm text-destructive">{status}</p>}
        </CardContent>
      </Card>

      {/* Quick links to detail pages */}
      <div className="flex flex-wrap gap-3 text-sm">
        <Link to="/market-target" className="text-primary hover:underline">
          Market Target settings
        </Link>
        <Link to="/alerts" className="text-primary hover:underline">
          Alerts
        </Link>
        <Link to="/link" className="text-primary hover:underline">
          Link brokerage
        </Link>
        <Link to="/settings" className="text-primary hover:underline">
          Notification settings
        </Link>
      </div>
    </section>
  );
}
